import React from "react";
import { useLocation } from "react-router-dom";
import * as yup from "yup";
import { LoginResponseDto } from "../../api/generated";
import { useAuthState } from "./AuthStateProvider";
import { useAuthActions } from "./AuthActionsProvider";
import { AxiosInstance } from "axios";
import {
  AuthStateContext,
  useAuthenticationState,
} from "./AuthStateProvider";
import { useApiInterceptors } from "../interceptors/useApiInterceptors";
import {
  AuthActionsContext,
  useAuthenticationActions,
} from "./AuthActionsProvider";

interface AuthProviderProps extends React.PropsWithChildren {
  axiosInstance: AxiosInstance;
}

const AuthProvider = React.memo(function AuthProvider(
  props: AuthProviderProps
) {
  const location = useLocation();
  const pathname = location.pathname;

  const authState = useAuthenticationState();

  const actions = useAuthenticationActions(authState);

  // Note: useRedirectUser functionality can be implemented separately if needed

  const memoizedHandleLogoutActionClick = React.useCallback(
    actions.handleLogoutActionClick,
    [actions.handleLogoutActionClick]
  );
  const memoizedHandleRefreshAccessToken = React.useCallback(
    actions.handleRefreshAccessToken,
    [actions.handleRefreshAccessToken]
  );

  const currentNumOfApiCalls = useApiInterceptors(
    props.axiosInstance,
    authState,
    memoizedHandleLogoutActionClick,
    memoizedHandleRefreshAccessToken,
    pathname
  );

  const authStateContextValue = React.useMemo(
    () => ({
      ...authState.authRef.current,
      isAuthenticated: authState.isAuthenticated,
      isLoginPending: actions.isLoginPending,
    }),
    [authState.authRef.current, authState.isAuthenticated, actions.isLoginPending]
  );

  const authActionsContextValue = React.useMemo(() => actions, [actions]);

  return (
    <AuthStateContext.Provider value={authStateContextValue}>
      <AuthActionsContext.Provider value={authActionsContextValue}>
        {props.children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
});

export default AuthProvider;

export { useAuthState, useAuthActions };

export namespace AuthModule {
  export const AUTH_STATE_STORAGE_KEY = "UserAuthState";

  export enum UserRoles {
    Admin = "Admin",
    BookingAdmin = "BookingAdmin",
    ClinicAdmin = "ClinicAdmin",
    PatientUser = "PatientUser",
    Editor = "Editor",
  }

  export enum PortalType {
    Internal = "internal",
    Clinics = "clinics",
    Patients = "patients",
    Sedationists = "sedationists",
  }

  export interface LoginResponse extends LoginResponseDto {}

  const basicJwtSchema = yup.string().required();

  const tokenExpirationSchema = yup
    .string()
    .test("is-valid-date", "Invalid expiration date", function (value) {
      if (!value) return false;
      const expirationDate = new Date(value);
      return !isNaN(expirationDate.getTime());
    });

  const jwtTokenSchema = yup.object().shape({
    token: basicJwtSchema.required(),
    tokenExpiration: tokenExpirationSchema.required(),
  });

  const refreshTokenSchema = yup.object().shape({
    token: basicJwtSchema.required(),
    refreshTokenExpiration: tokenExpirationSchema.optional(),
  });

  const userRolesSchema = yup
    .string()
    .oneOf(Object.values(UserRoles), "Invalid user role");

  const userProfileSchema = yup.object().shape({
    id: yup.string().required(),
    userName: yup.string().optional(),
    email: yup.string().required(),
    firstName: yup.string().optional(),
    lastName: yup.string().optional(),
    roles: yup
      .array()
      .of(userRolesSchema)
      .required("Roles are required")
      .min(1, "At least one role is required"),
    permissions: yup.array().of(yup.string()).optional(),
  });

  export const loginResponseSchema = yup.object().shape({
    token: jwtTokenSchema.required(),
    refreshToken: refreshTokenSchema.required(),
    userProfile: userProfileSchema.required(),
  });

  export interface UserRequest {
    email: string;
    password: string;
  }

  export const userSchema: yup.ObjectSchema<UserRequest> = yup.object({
    email: yup.string().email().required("Email is required").default(""),
    password: yup.string().required("Password is required").default(""),
  });
}
